<template>
  <div class="drawdown" data-testid="drawdown-input">
    <header class="drawdown__header">
      <div class="drawdown__header-content">
        <BaseText size="p4">Saldo alocado</BaseText>
        <BaseText size="p4" :color="NebraskaColors.elementPrimary">
          {{ balanceFormatted }}
        </BaseText>
      </div>
    </header>
    <div class="drawdown__main">
      <HeadingDisplay
        title="Prejuízo máximo definido"
        :subtitle="headingSubtitle"
      />
      <div class="drawdown__banner" data-testid="maintenance-banner">
        <BaseText size="p4">
          Estamos passando por um processo de
          <BaseText size="p4" weight="semibold" as="span"
            >evolução e manutenção</BaseText
          >. Durante esse período, a escolha manual do valor da zeragem de risco
          fica
          <BaseText size="p4" weight="semibold" as="span"
            >temporariamente desativada</BaseText
          >, e a zeragem automática passa de
          <BaseText size="p4" weight="semibold" as="span"
            >70% para 85%</BaseText
          >
          do valor alocado.
        </BaseText>
      </div>
      <div class="drawdown__slider">
        <div class="drawdown__slider-texts">
          <BaseText size="p2">{{ drawdownData.formatted }}</BaseText>
          <BaseText size="p2">{{ `${rangeValue}%` }}</BaseText>
        </div>
        <!-- Input temporariamente desabilitado -->
        <InputRange
          disabled
          v-model="rangeValue"
          data-testid="input-range"
          :max="maximumDailyLossPercentage"
          :min="marginAllocation.minimumDailyLossPercentage"
          :step="1"
        />
      </div>
      <DataTable
        data-testid="variation-table"
        class="drawdown__table"
        :columns="tableColumns"
        :data="tableData"
        :has-interaction="false"
        show-divider
      />
    </div>
    <ButtonContainer class="drawdown__button-container">
      <template #button-primary>
        <ButtonPrimary
          label="Continuar"
          size="large"
          :loading="dataForm.isUpdatingLimits"
          :disabled="isDisabled"
          @click="nextStep"
        />
      </template>
    </ButtonContainer>
  </div>
</template>

<script lang="ts" setup>
import { computed, inject, onMounted, Ref, ref } from 'vue';

import { NebraskaColors } from '@warrenbrasil/nebraska-tokens-web';
import {
  BaseText,
  HeadingDisplay,
  DataTable,
  DataTableProps,
  ButtonContainer,
  ButtonPrimary,
  InputRange
} from '@warrenbrasil/nebraska-web';

import {
  IMarginAllocationPreviewV2,
  IMarginAllocationV2
} from '@/modules/trade/services/margin-allocation';

import {
  EMarginAllocationSteps,
  formattedData
} from '@/modules/trade/views/MarginAllocationV2/helpers';
import { moneyFormat } from '@/modules/common/helpers/currency';

interface IData {
  balance: { value: number; formatted: string };
  isUpdatingLimits: boolean;
}

const changeStep = inject('changeStep', (step: EMarginAllocationSteps) => ({
  step
}));
const updateDataForm = inject(
  'updateDataForm',
  (key: string, value: object) => ({
    key,
    value
  })
);

const dataForm = inject('dataForm') as Ref<IData>;
const marginAllocationPreview = inject(
  'marginAllocationPreview'
) as Ref<IMarginAllocationPreviewV2>;
const marginAllocation = inject('marginAllocation') as Ref<IMarginAllocationV2>;

const maximumDailyLossPercentage = ref(85);

const rangeValue = ref(maximumDailyLossPercentage.value);
const balanceValue = ref(0);
const balanceFormatted = ref('');

const drawdownData = computed(() => {
  const value = Math.floor((rangeValue.value / 100) * balanceValue.value);

  return {
    value,
    formatted: moneyFormat(value)
  };
});

const headingSubtitle = computed(() => {
  return `O valor foi automaticamente definido em ${maximumDailyLossPercentage.value}% do saldo alocado.`;
});

const tableData = computed(() => {
  return formattedData(
    marginAllocationPreview.value.variationTable,
    balanceValue.value,
    drawdownData.value.value
  );
});

const tableColumns = [
  {
    label: 'Ativo',
    align: DataTableProps.Align.Left,
    getter: 'ticker'
  },
  {
    label: 'Exposição máxima',
    align: DataTableProps.Align.Right,
    getter: 'maxExpose'
  }
];

const isDisabled = computed(() => {
  return rangeValue.value < marginAllocation.value.minimumDailyLossPercentage;
});

function nextStep() {
  updateDataForm('drawdown', {
    value: drawdownData.value.value,
    formatted: drawdownData.value.formatted
  });
  updateDataForm('drawdownPercentage', {
    value: rangeValue.value / 100,
    formatted: `${rangeValue.value}%`
  });

  changeStep(EMarginAllocationSteps.REVIEW);
}

onMounted(() => {
  balanceValue.value = dataForm.value.balance.value;
  balanceFormatted.value = dataForm.value.balance.formatted;
});
</script>

<style lang="less" scoped>
@header-height: 130px;

.drawdown {
  &__header {
    background-color: @background-tertiary;

    &-content {
      display: flex;
      justify-content: space-between;
      max-width: 524px;
      margin: 0 auto;
      padding: @size-spacing-x450 @size-spacing-x400;
    }
  }

  &__main {
    max-width: 524px;
    margin: 0 auto;
    padding: @size-spacing-x1000 @size-spacing-x400 0;
  }

  &__banner {
    padding: @size-spacing-x400;
    background-color: @status-info-background;
    border-radius: @size-radius-x200;
    margin-top: @size-spacing-x400;
    margin-bottom: @size-spacing-x800;
  }

  &__slider {
    margin-top: @size-spacing-x800;

    &-texts {
      display: flex;
      justify-content: space-between;
      margin-bottom: @size-spacing-x400;
    }

    :deep(input[type='range']:disabled) {
      opacity: 0.5;
      cursor: not-allowed;
    }
  }

  &__input {
    margin-top: @size-spacing-x800;
  }

  &__table {
    margin-top: @size-spacing-x600;

    :deep(.cell) {
      padding: @size-spacing-x500 0;
    }

    :deep(.table-row:last-of-type) {
      td {
        border-bottom: none !important;
      }
    }
  }

  &__button-container {
    background-color: @background-secondary;
    width: 100%;
    position: fixed;
    bottom: 0;
    left: 0;
    z-index: 20;
  }
}
</style>
